import { useEffect, useRef } from "react";

/**
 * Global single-video controller.
 *
 * Guarantees:
 *  1. Only ONE <video> element across the entire app can play at a time.
 *     When any video starts playing, all others are paused immediately.
 *  2. When a playing <video> scrolls out of view, it auto-pauses.
 *  3. When the tab/page becomes hidden, the active video pauses.
 *
 * Usage: call `useGlobalVideoController(videoRef)` in any component
 * that renders a <video ref={videoRef} /> element.
 */

// Module-level registry of all attached video elements.
const registry = new Set<HTMLVideoElement>();
let currentlyPlaying: HTMLVideoElement | null = null;

const isVideoFullscreen = (video: HTMLVideoElement) => {
  if (typeof document === "undefined") return false;

  const doc = document as Document & {
    webkitFullscreenElement?: Element | null;
  };
  const webkitVideo = video as HTMLVideoElement & {
    webkitDisplayingFullscreen?: boolean;
  };

  return (
    document.fullscreenElement === video ||
    doc.webkitFullscreenElement === video ||
    webkitVideo.webkitDisplayingFullscreen === true
  );
};

const pauseAllExcept = (target: HTMLVideoElement) => {
  registry.forEach((v) => {
    if (v !== target && !v.paused) {
      try {
        v.pause();
      } catch {
        // ignore
      }
    }
  });
};

// Visibility listener — pause active video when tab hidden.
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.hidden && currentlyPlaying && !currentlyPlaying.paused) {
      try {
        currentlyPlaying.pause();
      } catch {
        // ignore
      }
    }
  });
}

export function useGlobalVideoController(
  videoRef: React.RefObject<HTMLVideoElement>
) {
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    registry.add(el);

    const handlePlay = () => {
      currentlyPlaying = el;
      pauseAllExcept(el);
    };

    const handleFullscreenChange = () => {
      if (isVideoFullscreen(el)) {
        currentlyPlaying = el;
        pauseAllExcept(el);
      }
    };

    const handlePause = () => {
      if (currentlyPlaying === el) currentlyPlaying = null;
    };

    const handleEnded = () => {
      if (currentlyPlaying === el) currentlyPlaying = null;
    };

    // When the user unmutes this video (e.g. via native fullscreen volume button),
    // immediately mute every other video so audio never overlaps. This also makes
    // the iOS fullscreen unmute button behave reliably.
    const handleVolumeChange = () => {
      if (!el.muted) {
        registry.forEach((v) => {
          if (v !== el) {
            try {
              v.muted = true;
            } catch {
              // ignore
            }
          }
        });
      }
    };

    el.addEventListener("play", handlePlay);
    el.addEventListener("pause", handlePause);
    el.addEventListener("ended", handleEnded);
    el.addEventListener("volumechange", handleVolumeChange);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange as EventListener);
    el.addEventListener("webkitbeginfullscreen", handleFullscreenChange as EventListener);
    el.addEventListener("webkitendfullscreen", handleFullscreenChange as EventListener);

    // Auto-pause when scrolled out of view.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && !el.paused && !isVideoFullscreen(el)) {
          try {
            el.pause();
          } catch {
            // ignore
          }
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(el);
    observerRef.current = observer;

    return () => {
      el.removeEventListener("play", handlePlay);
      el.removeEventListener("pause", handlePause);
      el.removeEventListener("ended", handleEnded);
      el.removeEventListener("volumechange", handleVolumeChange);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange as EventListener);
      el.removeEventListener("webkitbeginfullscreen", handleFullscreenChange as EventListener);
      el.removeEventListener("webkitendfullscreen", handleFullscreenChange as EventListener);
      observer.disconnect();
      registry.delete(el);
      if (currentlyPlaying === el) currentlyPlaying = null;
    };
  }, [videoRef]);
}

/** Imperative helper: pause every registered video. */
export function pauseAllVideos() {
  registry.forEach((v) => {
    if (!v.paused) {
      try {
        v.pause();
      } catch {
        // ignore
      }
    }
  });
  currentlyPlaying = null;
}
